import React, { useCallback, useReducer, useContext, useEffect } from 'react';
import { AppContext, IAppContext } from 'appState/appContext';
import { IUpdateRestaurantPropertiesAsPatchForDish } from 'types';
import { SaveAsPatchActionTypes } from './saveAsPatchModalActions';
import { saveAsPatchReducer } from './saveAsPatchModalReducer';
import { saveAsPatchInitialState, SaveAsPatchContext, ISaveAsPatchState } from './saveAsPatchModalContext';
import {
	IEditDishRestaurantPropertiesContext,
	EditDishRestaurantPropertiesContext,
} from '../../../../editDishRestaurantPropertiesState/editDishRestaurantPropertiesContext';
import { convertUndefinedFields } from 'utils/helpers';
import { updateDishRestaurantPropertiesAsPatch } from 'backend/dishes';

export const SaveAsPatchState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(saveAsPatchReducer, saveAsPatchInitialState);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const {
		refetchRestPropsData,
		changeTypeOfModalWindow: changeTypeOfParentModalWindow,
		dishId,
		customerName,
		fetchedRestaurants,
		IDSelectedRestaurant,
		price,
		isCreatedOnPos,
		hasOpenPrice,
		openPrice,
		openPriceMaxThreshold,
		taxPercent,
		hasSchedulePriority,
		activationSchedule,
		deactivationSchedule,
		inStop,
		isCommentVisible,
		isFiscalized,
		isTaxPercentProtectedFromSync,
		hasPortions,
	} = useContext<IEditDishRestaurantPropertiesContext>(EditDishRestaurantPropertiesContext);

	useEffect(() => {
		dispatch({
			type: SaveAsPatchActionTypes.SET_INITIAL_FORM_DATA,
			payload: {
				initialFormData: {
					dishId,
					customerName,
					fetchedRestaurants,
					IDSelectedRestaurantOnRestPropsPage: IDSelectedRestaurant,
					price,
					isCreatedOnPos,
					hasPortions,
					hasOpenPrice,
					openPrice,
					openPriceMaxThreshold,
					taxPercent,
					hasSchedulePriority,
					activationSchedule,
					deactivationSchedule,
					inStop,
					isCommentVisible,
					isFiscalized,
					isTaxPercentProtectedFromSync,
				},
			},
		});
	}, []);

	const saveData = useCallback(
		async (value: 'saveAndMakeOtherChanges' | 'saveAndClose') => {
			try {
				const dataForSubmit: IUpdateRestaurantPropertiesAsPatchForDish = {
					dishId,
					restaurantIds:
						state.typeOfChanges === 'basedChanges'
							? [
									state.initialFormData.IDSelectedRestaurantOnRestPropsPage || '',
									...state.IDsSelectedRestaurant,
							  ]
							: state.IDsSelectedRestaurant,
					update: {
						activationCron: {
							shouldUpdate: state.selectedProperties.includes('activation'),
							value:
								state.typeOfChanges === 'basedChanges'
									? state.initialFormData.activationSchedule
									: state.activationSchedule,
						},
						deactivationCron: {
							shouldUpdate: state.selectedProperties.includes('deactivation'),
							value:
								state.typeOfChanges === 'basedChanges'
									? state.initialFormData.deactivationSchedule
									: state.deactivationSchedule,
						},
						price: {
							shouldUpdate: state.selectedProperties.includes('price'),
							value: state.typeOfChanges === 'basedChanges' ? state.initialFormData.price : state.price,
						},
						taxPercent: {
							shouldUpdate: state.selectedProperties.includes('taxRate'),
							value:
								state.typeOfChanges === 'basedChanges'
									? state.initialFormData.taxPercent
									: state.taxPercent,
						},
						hasOpenPrice: {
							shouldUpdate: state.selectedProperties.includes('openPrice'),
							value:
								state.typeOfChanges === 'basedChanges'
									? state.initialFormData.hasOpenPrice
									: state.hasOpenPrice,
						},
						openPrice: {
							shouldUpdate: state.selectedProperties.includes('openPrice'),
							value:
								state.typeOfChanges === 'basedChanges' || !state.hasOpenPrice
									? state.initialFormData.openPrice
									: state.openPrice,
						},
						openPriceMaxThreshold: {
							shouldUpdate: state.selectedProperties.includes('openPrice'),
							value:
								state.typeOfChanges === 'basedChanges' || !state.hasOpenPrice
									? state.initialFormData.openPriceMaxThreshold
									: state.openPriceMaxThreshold,
						},
						isDisabled: {
							shouldUpdate: state.selectedProperties.includes('isDisabled'),
							value: state.typeOfChanges === 'basedChanges' ? state.initialFormData.inStop : state.inStop,
						},
						isCommentVisible: {
							shouldUpdate: state.selectedProperties.includes('comments'),
							value:
								state.typeOfChanges === 'basedChanges'
									? state.initialFormData.isCommentVisible
									: state.isCommentVisible,
						},
						isFiscalized: {
							shouldUpdate: state.selectedProperties.includes('isFiscalized'),
							value:
								state.typeOfChanges === 'basedChanges'
									? state.initialFormData.isFiscalized
									: state.isFiscalized,
						},
						isTaxPercentProtectedFromSync: {
							shouldUpdate: state.selectedProperties.includes('protectFromSync'),
							value:
								state.typeOfChanges === 'basedChanges'
									? state.initialFormData.isTaxPercentProtectedFromSync
									: state.isTaxPercentProtectedFromSync,
						},
					},
				};
				const convertedData = convertUndefinedFields(dataForSubmit);
				showLoader();
				await updateDishRestaurantPropertiesAsPatch(convertedData);
				if (value === 'saveAndClose') {
					changeTypeOfParentModalWindow(null);
					refetchRestPropsData(true);
				}
				if (value === 'saveAndMakeOtherChanges') {
					dispatch({ type: SaveAsPatchActionTypes.RESET_FIELDS });
				}
			} catch (err) {
				dispatch({
					type: SaveAsPatchActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
					payload: {
						typeOfModalWindow: 'error',
					},
				});
			} finally {
				hideLoader();
			}
		},
		[showLoader, hideLoader, state],
	);

	const setTypeOfChanges = useCallback(
		(value: ISaveAsPatchState['typeOfChanges']) => {
			dispatch({
				type: SaveAsPatchActionTypes.SET_TYPE_OF_CHANGES,
				payload: {
					typeOfChanges: value,
					IDsSelectedRestaurant:
						value === 'basedChanges'
							? state.IDsSelectedRestaurant.filter(
									(id) => id !== state.initialFormData.IDSelectedRestaurantOnRestPropsPage,
							  )
							: state.IDsSelectedRestaurant,
				},
			});
		},
		[state.IDsSelectedRestaurant, state.initialFormData.IDSelectedRestaurantOnRestPropsPage],
	);

	const setIDsRestaurant = useCallback((value: ISaveAsPatchState['IDsSelectedRestaurant']) => {
		dispatch({
			type: SaveAsPatchActionTypes.SET_IDS_RESTAURANT,
			payload: {
				IDsSelectedRestaurant: value,
			},
		});
	}, []);

	const setProperties = useCallback((value: ISaveAsPatchState['selectedProperties']) => {
		dispatch({
			type: SaveAsPatchActionTypes.SET_PROPERTIES,
			payload: {
				selectedProperties: value,
			},
		});
	}, []);

	const changePrice = useCallback((value: ISaveAsPatchState['price']) => {
		dispatch({
			type: SaveAsPatchActionTypes.CHANGE_PRICE,
			payload: {
				price: value,
			},
		});
	}, []);

	const toggleHasOpenPrice = useCallback(() => {
		dispatch({
			type: SaveAsPatchActionTypes.TOGGLE_HAS_OPEN_PRICE,
		});
	}, []);

	const changeOpenPrice = useCallback((value: ISaveAsPatchState['openPrice']) => {
		dispatch({
			type: SaveAsPatchActionTypes.CHANGE_OPEN_PRICE,
			payload: {
				openPrice: value,
			},
		});
	}, []);

	const changeOpenPriceMaxThreshold = useCallback((value: ISaveAsPatchState['openPriceMaxThreshold']) => {
		dispatch({
			type: SaveAsPatchActionTypes.CHANGE_OPEN_PRICE_MAX_THRESHOLD,
			payload: {
				openPriceMaxThreshold: value,
			},
		});
	}, []);

	const changeTaxPercent = useCallback((value: ISaveAsPatchState['taxPercent']) => {
		dispatch({
			type: SaveAsPatchActionTypes.CHANGE_TAX_PERCENT,
			payload: {
				taxPercent: value,
			},
		});
	}, []);

	const toggleInStop = useCallback(() => {
		dispatch({
			type: SaveAsPatchActionTypes.TOGGLE_IN_STOP,
		});
	}, []);

	const toggleIsCommentVisible = useCallback(() => {
		dispatch({
			type: SaveAsPatchActionTypes.TOGGLE_IS_COMMENT_VISIBLE,
		});
	}, []);

	const toggleIsFiscalized = useCallback(() => {
		dispatch({
			type: SaveAsPatchActionTypes.TOGGLE_IS_FISCALIZED,
		});
	}, []);

	const toggleIsTaxPercentProtectedFromSync = useCallback(() => {
		dispatch({
			type: SaveAsPatchActionTypes.TOGGLE_IS_TAX_PERCENT_PROTECT_FROM_SYNC,
		});
	}, []);

	const changeActivationSchedule = useCallback((value: ISaveAsPatchState['activationSchedule']) => {
		dispatch({
			type: SaveAsPatchActionTypes.CHANGE_ACTIVATION_SCHEDULE,
			payload: {
				activationSchedule: value,
			},
		});
	}, []);

	const changeDeactivationSchedule = useCallback((value: ISaveAsPatchState['deactivationSchedule']) => {
		dispatch({
			type: SaveAsPatchActionTypes.CHANGE_DEACTIVATION_SCHEDULE,
			payload: {
				deactivationSchedule: value,
			},
		});
	}, []);

	const changeTypeOfModalWindow = useCallback((value: ISaveAsPatchState['typeOfModalWindow']) => {
		dispatch({
			type: SaveAsPatchActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	}, []);

	return (
		<SaveAsPatchContext.Provider
			value={{
				saveData,
				setTypeOfChanges,
				setIDsRestaurant,
				setProperties,
				changePrice,
				toggleHasOpenPrice,
				changeOpenPrice,
				changeOpenPriceMaxThreshold,
				changeTaxPercent,
				toggleInStop,
				toggleIsCommentVisible,
				toggleIsFiscalized,
				toggleIsTaxPercentProtectedFromSync,
				changeActivationSchedule,
				changeDeactivationSchedule,
				changeTypeOfModalWindow,
				initialFormData: state.initialFormData,
				typeOfChanges: state.typeOfChanges,
				IDsSelectedRestaurant: state.IDsSelectedRestaurant,
				selectedProperties: state.selectedProperties,
				price: state.price,
				hasOpenPrice: state.hasOpenPrice,
				openPrice: state.openPrice,
				openPriceMaxThreshold: state.openPriceMaxThreshold,
				taxPercent: state.taxPercent,
				inStop: state.inStop,
				isCommentVisible: state.isCommentVisible,
				isFiscalized: state.isFiscalized,
				isTaxPercentProtectedFromSync: state.isTaxPercentProtectedFromSync,
				activationSchedule: state.activationSchedule,
				deactivationSchedule: state.deactivationSchedule,
				typeOfModalWindow: state.typeOfModalWindow,
			}}>
			{children}
		</SaveAsPatchContext.Provider>
	);
};
